// src/pages/Search.js
// Page résultats de recherche avec repli automatique sur données de démo - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ListingGrid from '../components/listing/ListingGrid';
import { useTheme } from '../hooks/useTheme';
import apiClient from '../api/client';
import DEMO_LISTINGS from '../data/demoListings';

// Distance approximative entre deux points GPS (formule haversine) - utilisée pour le repli local
const getDistanceKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const Search = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const lat = searchParams.get('lat');
  const lng = searchParams.get('lng');
  const query = searchParams.get('q');
  const isGeoSearch = lat && lng;

  useEffect(() => {
    const loadResults = async () => {
      setLoading(true);
      try {
        let listings = [];

        if (isGeoSearch) {
          const result = await apiClient.get(
            `/endpoints/listings.php?action=nearby&lat=${lat}&lng=${lng}&radius=25`
          );
          listings = result.listings && result.listings.length > 0 ? result.listings : null;
        } else {
          const result = await apiClient.get('/endpoints/listings.php?action=list&status=published&limit=100');
          listings = result.listings && result.listings.length > 0 ? result.listings : null;
          if (listings && query) {
            const q = query.toLowerCase();
            listings = listings.filter(
              (l) =>
                l.title?.toLowerCase().includes(q) ||
                l.quartier?.toLowerCase().includes(q) ||
                l.city?.toLowerCase().includes(q)
            );
          }
        }

        if (!listings) throw new Error('empty');
        setResults(listings);
      } catch (err) {
        // API indisponible ou vide → repli sur les données de démo
        let fallback = DEMO_LISTINGS;

        if (isGeoSearch) {
          const userLat = parseFloat(lat);
          const userLng = parseFloat(lng);
          fallback = fallback
            .map((l) => ({ ...l, distanceKm: Math.round(getDistanceKm(userLat, userLng, l.lat, l.lng) * 10) / 10 }))
            .sort((a, b) => a.distanceKm - b.distanceKm);
        } else if (query) {
          const q = query.toLowerCase();
          fallback = fallback.filter(
            (l) =>
              l.title.toLowerCase().includes(q) ||
              l.quartier.toLowerCase().includes(q) ||
              l.city.toLowerCase().includes(q)
          );
        }

        setResults(fallback);
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [lat, lng, query, isGeoSearch]);

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px',
      position: 'sticky',
      top: 0,
      backgroundColor: theme.bg,
      zIndex: 50,
      borderBottom: `1px solid ${theme.borderSoft}`,
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: theme.textPrimary,
      fontSize: '20px',
      cursor: 'pointer',
      padding: '4px',
    },
    title: { fontSize: '18px', fontWeight: '700', color: theme.textPrimary },
    subtitle: { padding: '8px 16px', fontSize: '14px', color: theme.textSecondary },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Résultats de recherche | Fynho</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.title}>
          {isGeoSearch ? `🧭 ${t('home.useLocation')}` : t('common.search')}
        </div>
      </div>

      {!loading && (
        <div style={styles.subtitle}>
          {results.length} {results.length > 1 ? t('home.listings') : t('home.listing')}
          {isGeoSearch && ' · triées par proximité'}
        </div>
      )}

      <ListingGrid listings={results} loading={loading} />
    </div>
  );
};

export default Search;